import { defineStore } from 'pinia'

import type { ProductCustomisations, ProductState as State } from '~/types/product'
import type { CartItem, CartItemIngredient, MenuItem } from '~/types/api/data-contracts'
import type { MenuItemChoice } from '~/types/menu'

import { useProductCommon } from '~/store/productStoreCommon'
import type { AllergenOption } from '~/components/product/GAllergenSelector/GAllergenSelector.types'
import { useProductStore } from '~/store'

export function useProductChoiceStore() {
  const initialState = (): State => ({
    id: undefined,
    product: undefined,
    fetching: {
      product: false
    },
    fetchDidFail: {
      product: false
    },
    quantity: 1,
    selectedSingleChoiceItems: undefined,
    customisedItem: undefined,
    activeCustomChoice: undefined,
    activeCustomChoiceIndex: undefined,
    customisations: {} as ProductCustomisations
  })

  const definedStores = new Map<string, ReturnType<typeof defineProductChoiceStore>>()

  function storeDefinition(id: string) {
    if (!definedStores.has(id)) {
      definedStores.set(id, defineProductChoiceStore(id))
    }

    return definedStores.get(id) as ReturnType<typeof defineProductChoiceStore>
  }

  const store = (id: string) => {
    return storeDefinition(id)()
  }

  function resetProductChoiceStores() {
    // Reset the stores
    definedStores.forEach((storeDefinition) => storeDefinition().$reset())
  }

  function clearStores() {
    // Clear the stores
    definedStores.forEach((storeDefinition) => storeDefinition().clearStore())
  }

  function defineProductChoiceStore<Id extends string>(id: Id) {
    return defineStore(`choice/${id}`, {
      state: initialState,

      getters: {
        getProductId(): number {
          return useProductCommon().getProductId(this)
        },
        /**
         * @description Gets all single choices available for the product
         */
        getAllSingleChoices(): MenuItemChoice[] {
          return useProductCommon().getAllSingleChoices(this)
        },
        /**
         * @description Returns true if the product has any single choices available
         */
        hasChoices(): boolean {
          return useProductCommon().hasChoices(this)
        },
        /**
         * @description Gets all multi choices available for the product
         */
        getAllMultiChoices(): MenuItemChoice[] {
          return useProductCommon().getAllMultiChoices(this)
        },
        /**
         * @description Gets the selected choice item for a given choice title
         * @param choiceTitle {MenuItemChoice["title"]} - The title of the choice
         * @returns {MenuItem | undefined} - The selected choice item or undefined if not found
         */
        getSelectedChoiceItem: (state) => {
          return (choiceTitle: MenuItemChoice['title']): MenuItem | undefined => {
            return useProductCommon().getSelectedChoiceItem(state, choiceTitle)
          }
        },
        /**
         * @description Gets the selected choice item ID for a given choice title
         * @param choiceTitle {MenuItemChoice["title"]} - The title of the choice
         * @returns {MenuItem["id"] | undefined} - The selected choice item ID or undefined if not found
         */
        getSelectedChoiceItemId: (state) => {
          return (choiceTitle: MenuItemChoice['title']): MenuItem['id'] | undefined => {
            return useProductCommon().getSelectedChoiceItemId(state, choiceTitle)
          }
        },
        getSelectedCustomChoices: (state) => {
          return (choiceTitle: MenuItemChoice['title']): Array<CartItem | undefined> | undefined => {
            return useProductCommon().getSelectedCustomChoices(state, choiceTitle)
          }
        },
        /**
         * @description Gets the selected multi-choice item for a given choice title
         * @param choiceTitle {MenuItemChoice["title"]} - The title of the choice
         * @returns {MenuItemChoice | undefined} - The selected choice item or undefined if not found
         */
        getSelectedMultiChoiceItem: (state) => {
          return (choiceTitle: MenuItemChoice['title']): MenuItemChoice | undefined => {
            return useProductCommon().getSelectedMultiChoiceItem(state, choiceTitle)
          }
        },
        /**
         * @description Returns true if the product has any multi-choices available
         */
        hasMultiChoices(): boolean {
          return useProductCommon().hasMultiChoices(this)
        },
        getMultiChoiceError: (state) => {
          return (choiceTitle: MenuItemChoice['title']): { isError: boolean; error?: string } | undefined => {
            return useProductCommon().getMultiChoiceError(state, choiceTitle)
          }
        },
        /**
         * @description The amount of additions available for the product
         */
        getCustomisationsAmount(): number {
          return useProductCommon().getCustomisationsAmount(this)
        },
        /**
         * @description Returns the price of the product in cents including customisations
         */
        getPriceInCents(): number {
          return useProductCommon().getPriceInCents(this)
        },
        /**
         * @description Returns the price of all customisations and selections, excluding the base product price
         */
        getPriceForFreeBundleInCents(): number {
          const totalPrice = this.getPriceInCents
          const basePrice = this.product?.priceInCents || 0
          return totalPrice - basePrice
        },
        /**
         * @description Returns the price of all customisations and selections, including the base product price
         */
        getPriceForBundleInCents(): number {
          return this.getPriceInCents
        }
      },

      actions: {
        /**
         * @description Updates the selected choice item for a given choice title
         * @param choiceTitle The title of the choice
         * @param item The selected choice item
         */
        updateSelectedChoiceItem(choiceTitle: MenuItemChoice['title'], item: MenuItem): void {
          useProductCommon().updateSelectedChoiceItem(this, choiceTitle, item)
        },
        /**
         * @description Updates the selected multi choice item for a given choice title
         * @param choiceTitle The title of the choice
         * @param item The selected choice item
         */
        updateSelectedMultiChoice(choiceTitle: MenuItemChoice['title'], choices: MenuItemChoice): void {
          useProductCommon().updateSelectedMultiChoice(this, choiceTitle, choices)
        },
        updateSelectedCustomChoice(
          choiceTitle: MenuItemChoice['title'],
          index: number,
          choice: CartItem | undefined
        ): void {
          useProductCommon().updateSelectedCustomChoice(this, choiceTitle, index, choice)
        },
        updateSelectedAllergen(allergen: AllergenOption): void {
          useProductCommon().updateSelectedAllergen(this, allergen)
        },
        updateAddition(newAddition: CartItemIngredient): void {
          useProductCommon().updateAddition(this, newAddition)
        },
        updateSubtraction(subtraction: CartItemIngredient): void {
          useProductCommon().updateSubtraction(this, subtraction)
        },
        /**
         * @description Initialises the product and upsell items as default
         * @param id {number} The product ID
         */
        async initProduct(id: number): Promise<void> {
          await useProductCommon().initProduct(this, id)
        },
        /**
         * @description Initialises the product and upsell items with customised data
         * @param cartItem {CartItem} The cart item to initialise the product with
         */
        async initCustomisedProduct(cartItem: CartItem): Promise<void> {
          await useProductCommon().initCustomisedProduct(this, cartItem)
        },
        /**
         * @description Adds the product and all upsell items to the cart
         */
        async addToBundle(
          thumbnailImageUrl: string | null | undefined,
          parentStoreId: string | undefined
        ): Promise<void> {
          const parentProductStore = parentStoreId ? useProductChoiceStore().store(parentStoreId) : useProductStore()

          const choiceTitle = parentProductStore.activeCustomChoice?.title
          const index = parentProductStore.activeCustomChoiceIndex
          if (!choiceTitle) {
            console.warn(`No choice found for choice title`)
            return
          }
          const choice = parentProductStore.getSelectedCustomChoices(choiceTitle)
          if (choice === undefined) {
            console.warn(`No choice found for choice title`, choiceTitle)
            return
          }
          if (index === undefined) {
            console.warn(`No active choice index`, choiceTitle)
            return
          } else {
            const cartItem = this.prepareProductForCart()
            cartItem.thumbnailImageUrl = thumbnailImageUrl
            choice[index] = cartItem
          }
        },
        /**
         * @description Prepares the product for adding to the cart
         * @returns {CartItem} The product in the format required for the cart
         */
        prepareProductForCart(): CartItem {
          return useProductCommon().prepareProductForCart(this)
        },
        /**
         * @description Validates the selected choice items are within the constraints
         */
        validateMultiSelectChoices(): boolean {
          return useProductCommon().validateMultiSelectChoices(this)
        },
        /**
         * @description Resets some of the store properties. Used when entering page with customised data
         */
        clearStore(): void {
          this.id = undefined
          this.product = undefined
          this.quantity = 1
        },
        reset(): void {
          this.$reset()
        }
      }
    })
  }

  return {
    store,
    resetProductChoiceStores,
    clearStores
  }
}
